import Typography from '@mui/material/Typography';
import { format, isAfter } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRightStraight, Logo } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Icon } from 'shared/components/ui/icon';
import { useToday } from 'shared/contexts/today';
import { useRedirectToStripeUrlOrStartTrial } from 'shared/hooks/use-redirect-to-stripe-url-or-start-trial';
import { useSubscriptionPrices } from 'shared/hooks/use-subscription-prices';
import { useUser } from 'shared/hooks/use-user';
import { PriceInterval } from 'shared/types/price';
import {
  Interval,
  Store,
  SubscriptionType,
} from 'shared/types/user-subscription';

import * as Styled from './settings-form.style';
import { SubscriptionManagementLinks } from './subscription-management-links';

export const PaymentManagement: React.FC = () => {
  const user = useUser();
  const { prices } = useSubscriptionPrices();
  const today = useToday();
  const { t } = useTranslation();
  const {
    redirectOrStartTrial: redirectToStripeUrl,
    isLoading: lifetimePriceLoading,
  } = useRedirectToStripeUrlOrStartTrial();

  // lifetime
  const lifetimePrice = useMemo(
    () => prices.find(({ interval }) => interval === PriceInterval.Lifetime),
    [prices],
  );

  const activeLifetime = useMemo(
    () =>
      user?.subscriptions
        ?.filter(({ interval }) => interval === Interval.Lifetime)
        .sort(
          ({ purchasedAt: purchasedAtA }, { purchasedAt: purchasedAtB }) =>
            new Date(purchasedAtB).getTime() - new Date(purchasedAtA).getTime(),
        )[0],
    [user?.subscriptions],
  );

  const lifeTimeIsTrial = !!activeLifetime?.expiresAt;
  const lifeTimePurchased =
    !lifeTimeIsTrial &&
    [SubscriptionType.Paid, SubscriptionType.Premium].includes(
      activeLifetime?.type as SubscriptionType,
    );
  const lifeTimeTrialExpired =
    !!activeLifetime?.expiresAt && isAfter(today, activeLifetime.expiresAt);

  const showLifetimePrice =
    !!lifetimePrice && !activeLifetime && !!user?.subscriptions?.length;

  const onPurchaseLifetime = () => {
    if (!lifetimePrice) {
      return;
    }

    redirectToStripeUrl({
      interval: lifetimePrice.interval,
      coupon: lifetimePrice.coupon?.id,
    });
  };

  // promotional
  const promotionalSubscription = useMemo(
    () =>
      user?.subscriptions
        ?.filter(
          ({ store, productId }) =>
            store === Store.Promotional && !productId?.includes('custom'),
        )
        .sort(
          ({ purchasedAt: purchasedAtA }, { purchasedAt: purchasedAtB }) =>
            new Date(purchasedAtB).getTime() - new Date(purchasedAtA).getTime(),
        )[0],
    [user?.subscriptions],
  );

  // subscriptions
  const activeSubscription = useMemo(
    () =>
      user?.subscriptions
        ?.filter(
          ({ interval, store }) =>
            interval !== Interval.Lifetime && store !== Store.Promotional,
        )
        .sort(
          ({ purchasedAt: purchasedAtA }, { purchasedAt: purchasedAtB }) =>
            new Date(purchasedAtB).getTime() - new Date(purchasedAtA).getTime(),
        )[0],
    [user?.subscriptions],
  );

  const activeSubscriptionIsApple =
    !activeSubscription?.stripeCustomerId &&
    !!activeSubscription?.managementUrl;
  const activeSubscriptionIsTrial =
    activeSubscription?.type === SubscriptionType.Trial;
  const activeSubscriptionPurchased =
    activeSubscription?.type === SubscriptionType.Paid;
  const activeSubscriptionCanceled = !!activeSubscription?.unsubscribedAt;

  return (
    <>
      {(!!activeSubscription || !!activeLifetime) && (
        <Styled.Section>
          <Typography variant="h6" component="span">
            {t('forms.user-settings.sections.subscriptions.label')}
          </Typography>

          {!!activeLifetime && (
            <Styled.SplitSectionRow>
              <Styled.SubscriptionContainer>
                <Styled.SubscriptionHeader>
                  <Styled.SubscriptionHeaderContent>
                    <Styled.SubscriptionIcon>
                      <Icon icon={Logo} />
                    </Styled.SubscriptionIcon>

                    <Styled.SubscriptionHeaderTitle>
                      {t(
                        'forms.user-settings.sections.subscriptions.premium.lifetime.label',
                      )}

                      {(lifeTimePurchased ||
                        lifeTimeIsTrial ||
                        lifeTimeTrialExpired) && (
                        <Styled.Pill
                          $success={lifeTimePurchased}
                          $warning={lifeTimeIsTrial}
                          $error={lifeTimeTrialExpired}
                        >
                          {t(
                            lifeTimeTrialExpired
                              ? 'forms.user-settings.sections.subscriptions.premium.lifetime.pill.trial-expired'
                              : lifeTimeIsTrial
                                ? 'forms.user-settings.sections.subscriptions.premium.lifetime.pill.trial'
                                : 'forms.user-settings.sections.subscriptions.premium.lifetime.pill.purchased',
                          )}
                        </Styled.Pill>
                      )}
                    </Styled.SubscriptionHeaderTitle>
                  </Styled.SubscriptionHeaderContent>

                  {!lifeTimePurchased && !!lifetimePrice && (
                    <Button
                      onClick={onPurchaseLifetime}
                      size={Sizes.Small}
                      variant={Variants.Outlined}
                      color={Colors.Inherit}
                      disabled={
                        lifetimePriceLoading || activeSubscriptionIsApple
                      }
                      endIcon={ArrowUpRightStraight}
                    >
                      {t(
                        'forms.user-settings.sections.subscriptions.premium.lifetime.button.label',
                      )}
                      {lifetimePriceLoading && (
                        <Styled.ButtonLoader color="primary" size={12} />
                      )}
                    </Button>
                  )}
                </Styled.SubscriptionHeader>

                <Styled.SubscriptionDescription>
                  {t(
                    lifeTimeTrialExpired
                      ? 'forms.user-settings.sections.subscriptions.premium.lifetime.trial-expired.description'
                      : lifeTimeIsTrial
                        ? 'forms.user-settings.sections.subscriptions.premium.lifetime.trial.description'
                        : 'forms.user-settings.sections.subscriptions.premium.lifetime.purchased.description',
                    {
                      date:
                        lifeTimeIsTrial || lifeTimeTrialExpired
                          ? activeLifetime.expiresAt
                            ? format(activeLifetime.expiresAt, 'd MMM yyyy')
                            : undefined
                          : format(activeLifetime.purchasedAt, 'd MMM yyyy'),
                    },
                  )}
                </Styled.SubscriptionDescription>
              </Styled.SubscriptionContainer>
            </Styled.SplitSectionRow>
          )}

          {!!activeSubscription && (
            <Styled.SplitSectionRow>
              <Styled.SubscriptionContainer>
                <Styled.SubscriptionHeader>
                  <Styled.SubscriptionHeaderContent>
                    <Styled.SubscriptionIcon>
                      <Icon icon={Logo} />
                    </Styled.SubscriptionIcon>

                    <Styled.SubscriptionHeaderTitle>
                      {t(
                        'forms.user-settings.sections.subscriptions.premium.subscription.label',
                      )}

                      {(activeSubscriptionPurchased ||
                        activeSubscriptionIsTrial ||
                        activeSubscriptionCanceled) && (
                        <Styled.Pill
                          $success={activeSubscriptionPurchased}
                          $warning={activeSubscriptionIsTrial}
                          $error={activeSubscriptionCanceled}
                        >
                          {t(
                            activeSubscriptionCanceled
                              ? 'forms.user-settings.sections.subscriptions.premium.pill.canceled'
                              : activeSubscriptionIsTrial
                                ? 'forms.user-settings.sections.subscriptions.premium.pill.trial'
                                : 'forms.user-settings.sections.subscriptions.premium.pill.active',
                          )}
                        </Styled.Pill>
                      )}
                    </Styled.SubscriptionHeaderTitle>
                  </Styled.SubscriptionHeaderContent>

                  {!!(
                    activeSubscription.managementUrl ||
                    activeSubscription.stripeCustomerId
                  ) && (
                    <SubscriptionManagementLinks
                      managementUrl={activeSubscription.managementUrl}
                      stripeCustomerId={activeSubscription.stripeCustomerId}
                      isApple={activeSubscriptionIsApple}
                    />
                  )}
                </Styled.SubscriptionHeader>

                <Styled.SubscriptionDescription>
                  {t(
                    activeSubscriptionCanceled
                      ? 'forms.user-settings.sections.subscriptions.premium.expires.description'
                      : activeSubscriptionIsTrial
                        ? 'forms.user-settings.sections.subscriptions.premium.trial.description'
                        : 'forms.user-settings.sections.subscriptions.premium.description',
                    {
                      date: activeSubscription.expiresAt
                        ? format(activeSubscription.expiresAt, 'd MMM yyyy')
                        : undefined,
                    },
                  )}
                </Styled.SubscriptionDescription>
              </Styled.SubscriptionContainer>
            </Styled.SplitSectionRow>
          )}

          {showLifetimePrice && !!lifetimePrice && (
            <Styled.SplitSectionRow>
              <Styled.SubscriptionContainer>
                <Styled.SubscriptionHeader>
                  <Styled.SubscriptionHeaderContent>
                    <Styled.SubscriptionIcon>
                      <Icon icon={Logo} />
                    </Styled.SubscriptionIcon>

                    <Styled.SubscriptionHeaderTitle>
                      {t(
                        'forms.user-settings.sections.subscriptions.premium.lifetime.label',
                      )}
                    </Styled.SubscriptionHeaderTitle>
                  </Styled.SubscriptionHeaderContent>
                  <Button
                    onClick={onPurchaseLifetime}
                    size={Sizes.Small}
                    variant={Variants.Outlined}
                    color={Colors.Inherit}
                    disabled={lifetimePriceLoading}
                    endIcon={ArrowUpRightStraight}
                  >
                    {t(
                      'forms.user-settings.sections.subscriptions.premium.lifetime.button.label',
                    )}
                    {lifetimePriceLoading && (
                      <Styled.ButtonLoader color="primary" size={12} />
                    )}
                  </Button>
                </Styled.SubscriptionHeader>

                <Styled.SubscriptionDescription>
                  {t(
                    'forms.user-settings.sections.subscriptions.premium.lifetime.description',
                  )}
                </Styled.SubscriptionDescription>
              </Styled.SubscriptionContainer>
            </Styled.SplitSectionRow>
          )}

          {!!promotionalSubscription && (
            <Styled.SplitSectionRow>
              <Styled.SubscriptionContainer>
                <Styled.SubscriptionHeader>
                  <Styled.SubscriptionHeaderContent>
                    <Styled.SubscriptionIcon>
                      <Icon icon={Logo} />
                    </Styled.SubscriptionIcon>

                    <Styled.SubscriptionHeaderTitle>
                      {t(
                        'forms.user-settings.sections.subscriptions.premium.promotional.label',
                      )}

                      <Styled.Pill $success $warning={false} $error={false}>
                        {t(
                          'forms.user-settings.sections.subscriptions.premium.promotional.pill.active',
                        )}
                      </Styled.Pill>
                    </Styled.SubscriptionHeaderTitle>
                  </Styled.SubscriptionHeaderContent>
                </Styled.SubscriptionHeader>

                {!!promotionalSubscription.expiresAt && (
                  <Styled.SubscriptionDescription>
                    {t(
                      'forms.user-settings.sections.subscriptions.premium.promotional.description',
                      {
                        date: format(
                          promotionalSubscription.expiresAt,
                          'd MMM yyyy',
                        ),
                      },
                    )}
                  </Styled.SubscriptionDescription>
                )}
              </Styled.SubscriptionContainer>
            </Styled.SplitSectionRow>
          )}
        </Styled.Section>
      )}
    </>
  );
};
